import { isValid as isValidDate, parseISO, startOfToday } from "date-fns";
import type { StateAction } from "@entrilia/shared/types";
import { invalidResult, requiredValidator, validResult } from "@entrilia/shared/utilities/validators";
import type { ConsentContentSource, UserConsentDetails } from "../../../../api/types/userConsentTypes";

type FormField = "confirmationText" | "content" | "activationDate" | "isActive";

export interface UserConsentEditState {
  confirmationText: string;
  htmlContent: string;
  contentSource: ConsentContentSource;
  activationDate: Date | null;
  isActive: boolean;
  touchedFields: FormField[];
  validationResult: ValidationResult;
  isEditingContent: boolean;
  editedHtmlContent: string;
  isImportingFile: boolean;
}

export const getInitialUserConsentEditState = (): UserConsentEditState => ({
  confirmationText: "",
  htmlContent: "",
  contentSource: "HtmlEditor",
  activationDate: null,
  isActive: false,
  touchedFields: [],
  validationResult: {
    isValid: false,
    errors: {},
  },
  isEditingContent: false,
  editedHtmlContent: "",
  isImportingFile: false,
});

// Validation

interface ValidationResult {
  isValid: boolean;
  errors: Partial<Record<FormField, string | undefined>>;
}

export const validateUserConsentEditState = (state: UserConsentEditState): ValidationResult => {
  const confirmationTextValidationResult = requiredValidator(state.confirmationText);
  const confirmationTextError = state.touchedFields.includes("confirmationText")
    ? confirmationTextValidationResult.error
    : undefined;

  const contentValidationResult = requiredValidator(state.htmlContent);
  const contentError = state.touchedFields.includes("content") ? contentValidationResult.error : undefined;

  const activationDateValidationResult =
    state.isActive && (state.activationDate === null || !isValidDate(state.activationDate))
      ? invalidResult("Invalid date")
      : validResult();

  const activationDateError = state.touchedFields.includes("activationDate")
    ? activationDateValidationResult.error
    : undefined;

  const isValid =
    confirmationTextValidationResult.isValid &&
    contentValidationResult.isValid &&
    activationDateValidationResult.isValid;

  return {
    isValid,
    errors: {
      confirmationText: confirmationTextError,
      content: contentError,
      activationDate: activationDateError,
    },
  };
};

// Actions

export const initFromConsentDetailsAction =
  (consentDetails: UserConsentDetails): StateAction<UserConsentEditState> =>
  (state) => ({
    ...state,
    confirmationText: consentDetails.customAgreementText || `I agree to ${consentDetails.name}`,
    htmlContent: consentDetails.content ?? "",
    contentSource: consentDetails.contentSource ?? "HtmlEditor",
    activationDate: consentDetails.activeFrom ? parseISO(consentDetails.activeFrom) : null,
    isActive: !!consentDetails.activeFrom,
    touchedFields: [],
    validationResult: {
      isValid: true,
      errors: {},
    },
  });

export const updateConfirmationTextAction =
  (confirmationText: string): StateAction<UserConsentEditState> =>
  (state) => {
    const newState: UserConsentEditState = {
      ...state,
      confirmationText,
      touchedFields: state.touchedFields.includes("confirmationText")
        ? state.touchedFields
        : [...state.touchedFields, "confirmationText"],
    };

    newState.validationResult = validateUserConsentEditState(newState);
    return newState;
  };

export const updateIsActiveAction =
  (isActive: boolean): StateAction<UserConsentEditState> =>
  (state) => {
    const newState: UserConsentEditState = {
      ...state,
      isActive,
      activationDate: isActive && state.activationDate === null ? startOfToday() : state.activationDate,
      touchedFields: state.touchedFields.includes("isActive")
        ? state.touchedFields
        : [...state.touchedFields, "isActive"],
    };

    newState.validationResult = validateUserConsentEditState(newState);
    return newState;
  };

export const updateActivationDateAction =
  (activationDate: Date | null): StateAction<UserConsentEditState> =>
  (state) => {
    const newState: UserConsentEditState = {
      ...state,
      activationDate,
      touchedFields: state.touchedFields.includes("activationDate")
        ? state.touchedFields
        : [...state.touchedFields, "activationDate"],
    };

    newState.validationResult = validateUserConsentEditState(newState);
    return newState;
  };

export const startEditingContentAction = (): StateAction<UserConsentEditState> => (state) => ({
  ...state,
  isEditingContent: true,
  isImportingFile: false,
});

export const cancelEditingContentAction = (): StateAction<UserConsentEditState> => (state) => ({
  ...state,
  isEditingContent: false,
  editedHtmlContent: "",
});

export const finishEditingContentAction = (): StateAction<UserConsentEditState> => (state) => {
  const newState: UserConsentEditState = {
    ...state,
    isEditingContent: false,
    htmlContent: state.editedHtmlContent,
    editedHtmlContent: "",
    contentSource: "HtmlEditor",
    touchedFields: state.touchedFields.includes("content") ? state.touchedFields : [...state.touchedFields, "content"],
  };

  newState.validationResult = validateUserConsentEditState(newState);
  return newState;
};

export const editHtmlContentAction =
  (editedHtmlContent: string): StateAction<UserConsentEditState> =>
  (state) => ({
    ...state,
    editedHtmlContent,
  });

export const startImportFileAction = (): StateAction<UserConsentEditState> => (state) => ({
  ...state,
  isImportingFile: true,
  isEditingContent: false,
});

export const cancelImportFileAction = (): StateAction<UserConsentEditState> => (state) => ({
  ...state,
  isImportingFile: false,
});

export const finishImportFileAction =
  (htmlContent: string): StateAction<UserConsentEditState> =>
  (state) => {
    const newState: UserConsentEditState = {
      ...state,
      isImportingFile: false,
      htmlContent,
      contentSource: "ImportedContent",
      touchedFields: state.touchedFields.includes("content")
        ? state.touchedFields
        : [...state.touchedFields, "content"],
    };

    newState.validationResult = validateUserConsentEditState(newState);
    return newState;
  };
